import { Apple, ArrowDown, ArrowUpRight, Instagram, Play } from "lucide-react";
import { APP_STORE_URL, INSTAGRAM_URL, PLAY_STORE_URL } from "@/constants/site";
import { PhoneMockup } from "@/components/landing/PhoneMockup";
import { Reveal } from "@/components/landing/Reveal";

export const Hero = () => {
  return (
    <section id="top" className="relative overflow-hidden bg-splurge-bg">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-splurge-violet/25 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 top-40 h-[420px] w-[420px] rounded-full bg-splurge-primary/15 blur-3xl"
      />

      <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 pb-24 pt-16 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 lg:pb-32 lg:pt-24">
        <div>
          <div className="logo-rise mb-8" data-testid="hero-logo">
            <img
              src="/logo.png"
              alt="Splurge Finance logo"
              className="h-24 w-24 rounded-full shadow-2xl shadow-splurge-primary/30 ring-1 ring-splurge-lavender sm:h-28 sm:w-28"
            />
          </div>
          <Reveal>
            <span
              data-testid="hero-pill"
              className="inline-flex items-center gap-2 rounded-full border border-splurge-primary/25 bg-white px-4 py-1.5 text-xs font-bold tracking-wide text-splurge-dark shadow-sm"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-splurge-primary" aria-hidden="true" />
              Built for students, not spreadsheets
            </span>
          </Reveal>

          <Reveal delay={90}>
            <h1 className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-splurge-ink sm:text-5xl lg:text-6xl">
              Spend socially.
              <br />
              <span className="bg-gradient-to-r from-splurge-dark via-splurge-primary to-splurge-violet bg-clip-text text-transparent">
                Spend smarter.
              </span>
            </h1>
          </Reveal>

          <Reveal delay={180}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-splurge-ink/65 md:text-lg">
              Track the real cost of going out, settle expenses with friends, and build
              healthier money habits, without making life feel restrictive.
            </p>
          </Reveal>

          <Reveal delay={270}>
            <div className="mt-9 flex flex-col gap-3.5 sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-download-cta"
                aria-label="Download Splurge on the App Store (opens in a new tab)"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-splurge-primary px-7 py-3.5 text-sm font-bold text-white shadow-xl shadow-splurge-primary/35 transition-all duration-200 hover:-translate-y-0.5 hover:bg-splurge-dark hover:shadow-splurge-dark/35 active:scale-95"
              >
                <Apple className="h-4 w-4" aria-hidden="true" />
                App Store
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-android-cta"
                aria-label="Get Splurge on Google Play (opens in a new tab)"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-splurge-ink px-7 py-3.5 text-sm font-bold text-white shadow-xl shadow-splurge-ink/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-splurge-dark active:scale-95"
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                Google Play
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="#why"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-splurge-ink/12 bg-white px-7 py-3.5 text-sm font-bold text-splurge-ink transition-all duration-200 hover:-translate-y-0.5 hover:border-splurge-primary/40 hover:text-splurge-primary active:scale-95"
              >
                See what it does
                <ArrowDown className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={360}>
            <p className="mt-6 text-sm font-medium text-splurge-ink/50" data-testid="hero-supporting-line">
              Free on iOS &amp; Android · Designed for students aged 13–25
            </p>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-instagram-link"
              aria-label="Follow Splurge Finance on Instagram (opens in a new tab)"
              className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-splurge-ink/50 transition-colors hover:text-splurge-primary"
            >
              <Instagram className="h-4 w-4 text-splurge-primary" aria-hidden="true" />
              Follow @splurgefinance on Instagram
            </a>
          </Reveal>
        </div>

        <Reveal delay={200} className="lg:justify-self-center">
          <PhoneMockup />
        </Reveal>
      </div>
    </section>
  );
};
